import React, { useState, useEffect, useContext } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import errorReporter from '@app/errorReporter';
import APIService, { TeamController } from 'api';
import { isListingAvailable } from 'utils';
import { Icon, message } from 'components';

import { JsonObject } from '@bb/domain/dist/types';
import { IGenericUser, isTeamAdmin, UserRoles } from '@bb/users';
import type { SimpleListing, GetSimpleListingsByIdsDTO } from '@/app-package';

import {
  ContactDrawerMode,
  ContactDrawerType,
  ContactDrawerTeam,
  ContactMatchParams,
} from '../types';

import { ContactDrawerContext, AuthContext } from '../../../contexts';

import {
  ProfileContainer,
  ActionHeader,
  Container,
  ContentContainer,
  GoBackIconContainer,
} from './Content/common';
import { DetailsContainer } from './Details/common';

import TeamProfile from './Profile/TeamProfile';
import TeamDetails from './Details/TeamDetails';

interface DrawerContentProps extends RouteComponentProps<ContactMatchParams> {
  parentHistory: any;
  mode: ContactDrawerMode;
  type: ContactDrawerType;
}

const TeamDrawerContent: React.FC<DrawerContentProps> = props => {
  const { closeContactDrawer } = useContext(ContactDrawerContext);
  const { auth } = useContext(AuthContext);
  const { role, org, team, teams } = auth;

  let teamsStrings: string[] = [];
  if (isTeamAdmin(auth) && teams.length > 0) {
    teamsStrings = teams.map(toString);
  }

  const [isLoading, setIsLoading] = useState(true);
  const [teamContact, setTeamContact] = useState<ContactDrawerTeam>();
  const [teamListings, setTeamListings] = useState<
    GetSimpleListingsByIdsDTO['listings']
  >([]);

  useEffect(() => {
    const {
      match: { params },
    } = props;

    if (params.id) getTeamContact(params.id);
  }, []);

  const getTeamContact = async (teamId: string) => {
    try {
      const contact = await TeamController.get(teamId);

      if (contact.orgId === org.id) {
        const listings = await APIService.GET.getSimpleTeamListings(teamId);
        const sortedListings = listings.sort(sortListings);
        setTeamListings(sortedListings);
      }

      const sortedUsers = contact.users.sort(sortUsers);
      setTeamContact({
        ...contact,
        users: sortedUsers,
      });
    } catch (err) {
      message.error('Could not fetch team contact');
      errorReporter.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const sortUsers = (
    user1: JsonObject<IGenericUser>,
    user2: JsonObject<IGenericUser>,
  ) => {
    if (user1.role === 'TEAM_ADMIN' && user2.role !== 'TEAM_ADMIN') {
      return -1;
    }
    if (user1.role !== 'TEAM_ADMIN' && user2.role === 'TEAM_ADMIN') {
      return 1;
    }
    if (user1.firstName !== user2.firstName) {
      return user1.firstName.localeCompare(user2.firstName);
    }
    return user1.lastName.localeCompare(user2.lastName);
  };

  const sortListings = (listing1: SimpleListing, listing2: SimpleListing) => {
    const availability1 = isListingAvailable(listing1);
    const availability2 = isListingAvailable(listing2);

    if (availability1 && !availability2) {
      return -1;
    }
    if (!availability1 && availability2) {
      return 1;
    }
    if (availability1 && availability2) {
      return listing2.listedAt!.localeCompare(listing1.listedAt!);
    }

    return listing2.createdAt.localeCompare(listing1.createdAt);
  };

  const goToContact = (type: ContactDrawerType, contactId: string) => {
    const { history } = props;
    history.push(`/${type}/${contactId}`);
  };

  const goToListing = (listingId: string) => {
    const { parentHistory } = props;

    closeContactDrawer();
    parentHistory.push(`/listing/${listingId}/view`);
  };

  const goBack = () => {
    const { history } = props;

    history.goBack();
  };

  const handleEditIconClick = () => {
    const { parentHistory } = props;
    if (
      role === UserRoles['Global Admin'] ||
      role === UserRoles['Branch Admin']
    ) {
      closeContactDrawer();
      parentHistory.push(
        `/users/list?tabKey=teams&searchTeam=${teamContact?.name}&teamId=${teamContact?.id}`,
      );
    } else if (role === UserRoles['Team Admin']) {
      closeContactDrawer();
      parentHistory.push(`/team_settings`);
    }
  };

  const showEditBtn = () => {
    switch (role) {
      case UserRoles['Global Admin']:
      case UserRoles['Branch Admin']:
        return org?.id.toString() === teamContact?.orgId.toString();
      case UserRoles['Team Admin']:
        return (
          teamContact?.id &&
          (team?.toString() === teamContact.id ||
            teamsStrings?.includes(teamContact.id))
        );
      default:
        return false;
    }
  };

  return (
    <>
      <Container>
        <ActionHeader>
          {/*
          // @ts-ignore */}
          {props.history.index > 0 ? (
            <GoBackIconContainer
              onClick={() => goBack()}
              data-cy="contact-goBack-icon"
            >
              <Icon
                type="antdLeftOutlined"
                height={14}
                width={14}
                color={'black'}
              />
            </GoBackIconContainer>
          ) : (
            <>
              <GoBackIconContainer
                onClick={() => closeContactDrawer()}
                data-cy="contact-close-icon"
              >
                <Icon type="x-alt" width={12} height={12} color="black" />
              </GoBackIconContainer>
              {showEditBtn() && (
                <GoBackIconContainer
                  onClick={handleEditIconClick}
                  data-cy="contact-edit-icon"
                >
                  <Icon type="edit" width={20} height={20} color="black" />
                </GoBackIconContainer>
              )}
            </>
          )}
        </ActionHeader>
        <ContentContainer>
          <>
            <ProfileContainer>
              <TeamProfile isLoading={isLoading} teamContact={teamContact} />
            </ProfileContainer>

            <DetailsContainer>
              <TeamDetails
                isLoading={isLoading}
                teamContact={teamContact}
                teamListings={teamListings}
                goToContact={goToContact}
                goToListing={goToListing}
              />
            </DetailsContainer>
          </>
        </ContentContainer>
      </Container>
    </>
  );
};

export default TeamDrawerContent;
